import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { CONTACT } from 'config/contact';
import type { FAQItem } from '../types';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      id: '1',
      question: 'What is the difference between n8n and Make.com?',
      answer: 'n8n is an open-source, fair-code automation platform that offers self-hosting capabilities and unlimited executions, making it ideal for technical teams who want full control. Make.com (formerly Integromat) is a cloud-based visual automation platform with an intuitive drag-and-drop interface, perfect for teams who want quick deployment without technical complexity. Both are powerful tools, and we help you choose based on your specific needs, technical capabilities, and budget.'
    },
    {
      id: '2',
      question: 'How long does it take to implement a workflow automation?',
      answer: 'Implementation time varies based on complexity. Simple workflows (3-5 steps) typically take 1-2 weeks, intermediate workflows (6-10 steps) take 2-4 weeks, and advanced enterprise workflows can take 4-8 weeks. This includes planning, development, testing, and training. We provide detailed timelines during the initial consultation and keep you updated throughout the process.'
    },
    {
      id: '3',
      question: 'Do I need technical knowledge to use these platforms?',
      answer: 'No technical knowledge is required to use the workflows we build for you. Both n8n and Make.com offer visual, no-code interfaces that are intuitive to use. We provide comprehensive training and documentation so your team can confidently manage and monitor the automations. For more complex customizations, our support team is always available to help.'
    },
    {
      id: '4',
      question: 'What happens if an automation fails or encounters an error?',
      answer: 'All our workflows include robust error handling and monitoring. You receive immediate notifications if something goes wrong, and our systems automatically attempt to resolve common issues. We also provide detailed error logs and documentation. With our support packages, our team can quickly diagnose and fix any issues, often before they impact your operations.'
    },
    {
      id: '5',
      question: 'Can you integrate with custom or proprietary software?',
      answer: 'Yes! Both n8n and Make.com support custom API integrations, allowing us to connect virtually any software that has an API. If your tool does not have an API, we can often create workarounds using webhooks, email parsing, or other methods. During the consultation, we will assess your specific integration requirements and provide a detailed implementation plan.'
    },
    {
      id: '6',
      question: 'What kind of support do you provide after implementation?',
      answer: 'All packages include post-implementation support ranging from 30 days to 1 year depending on your tier. Support includes troubleshooting, minor adjustments, optimization recommendations, and training refreshers. Growth and Enterprise packages include regular review sessions to ensure your automations continue to meet your evolving needs. We also offer extended support contracts for ongoing maintenance and optimization.'
    },
    {
      id: '7',
      question: 'How much can I realistically save with workflow automation?',
      answer: 'Most businesses save 40-70 hours monthly through automation, translating to $30,000-$60,000 in annual cost savings. The exact savings depend on your current processes, team size, and automation complexity. During our free audit, we provide a detailed ROI calculation specific to your business, showing projected time savings, cost reductions, and efficiency improvements.'
    },
    {
      id: '8',
      question: 'What if I want to switch platforms later?',
      answer: 'While we recommend choosing the right platform from the start, migrations are possible. Both n8n and Make.com use similar logic structures, making transitions smoother than you might expect. If you start with one platform and later need to switch, we can help migrate your workflows with minimal disruption. Our Growth and Enterprise packages include platform flexibility to accommodate changing needs.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="HelpCircle" size={16} />
            <span>Common Questions</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-text-primary mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-text-secondary">
            Everything you need to know about workflow automation with n8n and Make.com
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={faq.id}
              className="bg-card rounded-xl border border-border overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-surface transition-colors duration-300"
              >
                <span className="font-semibold text-text-primary pr-4">{faq.question}</span>
                <Icon 
                  name="ChevronDown" 
                  size={24} 
                  className={`text-primary flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              
              {openIndex === index && (
                <div className="px-6 pb-6">
                  <p className="text-text-secondary leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-text-secondary mb-4">
            Still have questions? We're here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="inline-flex items-center space-x-2 bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300">
              <Icon name="Calendar" size={20} />
              <span>Schedule a Call</span>
            </button>
            <a href={CONTACT.whatsappLink} target="_blank" rel="noopener noreferrer">
              <button className="inline-flex items-center space-x-2 bg-success hover:bg-success/90 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300">
                <Icon name="MessageCircle" size={20} />
                <span>Chat on WhatsApp</span>
              </button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;